import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useEffortsByGroup, useEffortsFilterState } from '../hooks/useEffortsDashboard';
import { formatNumber } from '../../../utils/dashboardFormatters';
import {
  FIRST_COL_SX,
  HEADER_CELL_SX,
  NUM_CELL_SX,
  PctPill,
  SUBPILL_GROUP_SX,
  TABLE_CARD_SX,
  TABLE_SX,
  TABLE_WRAP_SX,
  TOTAL_ROW_SX,
  UNAVAIL_CELL_SX,
  UNAVAIL_HEADER_SX,
  TableCardTitle,
  TableFooter,
} from './EffortsTableParts.jsx';

const PILLS = [
  { key: 'division', label: 'Division', enabled: true },
  // Zone data isn't available yet (backend short-circuits group_by=zone to an
  // empty result). Render the pill disabled/greyed and non-selectable.
  { key: 'zone', label: 'Zone', enabled: false },
  { key: 'state', label: 'State', enabled: true },
  { key: 'bdm', label: 'BDM', enabled: true },
  { key: 'zm', label: 'ZM', enabled: true },
  { key: 'rm', label: 'RM', enabled: true },
  { key: 'hq', label: 'HQ', enabled: true },
];

/** Pills that need a single division selected to be meaningful. */
const NEEDS_SINGLE_DIVISION = new Set(['bdm', 'zm', 'rm']);

const ROWS_PER_PAGE = 25;

const fmt = (v, d = 1) => (v == null ? '—' : Number(v).toFixed(d));

const DASH = '—';
const UNAVAIL_TIP = 'Data not available yet';

/**
 * Division text for a group row: prefer the scalar `division`; when that is
 * null/empty but the backend sends a `divisions` list (a group spanning more
 * than one division), show them comma-separated. Returns null when neither is
 * present (caller renders a greyed em dash).
 */
function divisionText(g) {
  const single = g?.division;
  if (single != null && String(single).trim() !== '') return String(single);
  const list = Array.isArray(g?.divisions) ? g.divisions.filter(Boolean) : [];
  return list.length > 0 ? list.join(', ') : null;
}

/**
 * Columns the backend `by-group` payload does NOT return yet (verified in the
 * efforts service: a group carries only group/flsCount/avgWorkingDays/
 * drCallAverage/coveragePercent/compliancePercent). They render with a header
 * + greyed "—" cells and an "unavailable" tooltip, never hidden.
 */
const UNAVAILABLE_COLUMNS = [
  { key: 'nonFwd', label: 'Non FWD (Avg)', align: 'right' },
  { key: 'leavesPerFls', label: 'No. of Leaves / FLS', align: 'right' },
  { key: 'drsMissedPerFls', label: 'No. of DRs Missed / FLS', align: 'right' },
];

/** "202603" | "2026-03" -> "March 2026"; null/invalid -> "Latest". */
function monthLabel(ym) {
  const s = String(ym ?? '').replace(/-/g, '');
  if (!/^\d{6}$/.test(s)) return 'Latest';
  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(4, 6));
  if (!year || month < 1 || month > 12) return 'Latest';
  return `${new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' })} ${year}`;
}

/**
 * FLS-weighted average of `field` across ALL rows (never a page slice). Weight
 * is each row's flsCount; rows missing the value or weight are skipped. Returns
 * null when nothing contributes (caller renders "—").
 */
function weightedAvg(rows, field) {
  let num = 0;
  let den = 0;
  rows.forEach((r) => {
    const v = r?.[field];
    const w = Number(r?.flsCount) || 0;
    if (v == null || w <= 0) return;
    num += Number(v) * w;
    den += w;
  });
  return den > 0 ? num / den : null;
}

export default function EffortByGroupTab() {
  const [groupBy, setGroupBy] = useState('division');
  const [page, setPage] = useState(0);
  const { data, isLoading, isError } = useEffortsByGroup(groupBy);
  const { applied } = useEffortsFilterState();

  const groups = useMemo(() => data?.groups ?? [], [data]);
  const label = PILLS.find((p) => p.key === groupBy)?.label ?? 'Group';

  // Exactly one division applied → BDM/ZM/RM groupings are meaningful.
  const singleDivision = (applied?.division?.length ?? 0) === 1;

  // Reset to page 1 when the pill (group_by) changes or the applied filters
  // change (a filter Apply refetches, giving a new `groups` reference).
  const lastGroupsRef = useRef(groups);
  const lastGroupByRef = useRef(groupBy);
  if (lastGroupsRef.current !== groups || lastGroupByRef.current !== groupBy) {
    lastGroupsRef.current = groups;
    lastGroupByRef.current = groupBy;
    if (page !== 0) setPage(0);
  }

  // Slice only the body for the current page; any total row (if added) must be
  // computed from ALL `groups`, never from this slice.
  const pageCount = Math.max(1, Math.ceil(groups.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedGroups = groups.slice(
    safePage * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  // Totals over ALL groups (never the current page): sum FLS; FLS-weighted
  // averages for the metric columns (null -> "—").
  const totals = useMemo(() => {
    const totalFls = groups.reduce((s, g) => s + (Number(g.flsCount) || 0), 0);
    return {
      flsCount: totalFls,
      avgWorkingDays: weightedAvg(groups, 'avgWorkingDays'),
      coveragePercent: weightedAvg(groups, 'coveragePercent'),
      compliancePercent: weightedAvg(groups, 'compliancePercent'),
      drCallAverage: weightedAvg(groups, 'drCallAverage'),
    };
  }, [groups]);

  // Subtitle (reference `effortSubG`): "<n> <Dim> groups · <total FLS> active
  // FLS · <Mon>". <Dim> is the current group-by label.
  const subtitle = `${groups.length} ${label} groups · ${formatNumber(totals.flsCount)} active FLS · ${monthLabel(applied?.yearmonth)}`;

  // The Division column is shown ONLY when grouping by something OTHER than
  // Division (on Division it would just repeat the first column). On State /
  // BDM / ZM / RM / HQ it shows the real division from the backend row.
  const showDivisionCol = groupBy !== 'division';

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={groupBy}
        onChange={(_e, v) => v && setGroupBy(v)}
        sx={{ mb: 2, ...SUBPILL_GROUP_SX }}
      >
        {PILLS.map((p) => {
          // BDM/ZM/RM need one division; hint the user when none/many is applied.
          const needsDivision = NEEDS_SINGLE_DIVISION.has(p.key) && !singleDivision;
          const tip = !p.enabled
            ? 'Coming soon'
            : needsDivision
              ? 'Select one division'
              : '';
          return (
            <Tooltip key={p.key} title={tip}>
              {/* span wrapper so the tooltip still fires over a disabled button */}
              <span style={!p.enabled ? { cursor: 'not-allowed' } : undefined}>
                <ToggleButton
                  value={p.key}
                  disabled={!p.enabled}
                  sx={!p.enabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                >
                  {p.label}
                </ToggleButton>
              </span>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>

      {isLoading ? <Alert severity="info">Loading…</Alert> : null}
      {isError ? <Alert severity="error">Failed to load effort by group.</Alert> : null}
      {!isLoading && !isError && groups.length === 0 ? (
        <Alert severity="info">No data available.</Alert>
      ) : null}

      {groups.length > 0 ? (
        <Box sx={TABLE_CARD_SX}>
          <TableCardTitle title={`Effort by ${label}`} count={groups.length} />
          <Box sx={{ fontSize: '12px', color: '#6B7280', mb: 1.5, mt: -0.5 }}>{subtitle}</Box>
          <TableContainer sx={TABLE_WRAP_SX}>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEADER_CELL_SX}>{label}</TableCell>
                  {/* Division — only on non-Division sub-tabs; shows the real
                      division returned per group (no greying). */}
                  {showDivisionCol ? (
                    <TableCell sx={HEADER_CELL_SX}>Division</TableCell>
                  ) : null}
                  <TableCell align="right" sx={HEADER_CELL_SX}>No. of Active FLs</TableCell>
                  <TableCell align="right" sx={HEADER_CELL_SX}>Avg FWD</TableCell>
                  <TableCell align="right" sx={HEADER_CELL_SX}>Dr. Covg (Avg)</TableCell>
                  <TableCell align="right" sx={HEADER_CELL_SX}>Freq. Comp. (Avg)</TableCell>
                  <TableCell align="right" sx={HEADER_CELL_SX}>Call Avg</TableCell>
                  {/* Trailing greyed columns the backend doesn't return yet */}
                  {UNAVAILABLE_COLUMNS.map((c) => (
                    <Tooltip key={c.key} title={UNAVAIL_TIP}>
                      <TableCell align={c.align} sx={UNAVAIL_HEADER_SX}>{c.label}</TableCell>
                    </Tooltip>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedGroups.map((g) => (
                  <TableRow key={g.groupCode ?? g.group} hover>
                    <TableCell sx={FIRST_COL_SX}>{g.group}</TableCell>
                    {showDivisionCol ? (
                      (() => {
                        const divText = divisionText(g);
                        return (
                          <TableCell>
                            {divText == null ? (
                              <Box component="span" sx={{ color: '#b4bcc8' }}>{DASH}</Box>
                            ) : (
                              divText
                            )}
                          </TableCell>
                        );
                      })()
                    ) : null}
                    <TableCell align="right" sx={NUM_CELL_SX}>{formatNumber(g.flsCount)}</TableCell>
                    <TableCell align="right" sx={NUM_CELL_SX}>{fmt(g.avgWorkingDays)}</TableCell>
                    <TableCell align="right" sx={NUM_CELL_SX}>
                      <PctPill value={g.coveragePercent} />
                    </TableCell>
                    <TableCell align="right" sx={NUM_CELL_SX}>
                      <PctPill value={g.compliancePercent} />
                    </TableCell>
                    <TableCell align="right" sx={NUM_CELL_SX}>{fmt(g.drCallAverage, 2)}</TableCell>
                    <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                    <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                    <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                  </TableRow>
                ))}
                {/* Total row — pinned, bold, over ALL groups (not the page). */}
                <TableRow sx={TOTAL_ROW_SX}>
                  <TableCell>Total</TableCell>
                  {showDivisionCol ? <TableCell sx={UNAVAIL_CELL_SX}>{DASH}</TableCell> : null}
                  <TableCell align="right" sx={NUM_CELL_SX}>{formatNumber(totals.flsCount)}</TableCell>
                  <TableCell align="right" sx={NUM_CELL_SX}>{fmt(totals.avgWorkingDays)}</TableCell>
                  <TableCell align="right" sx={NUM_CELL_SX}>
                    {totals.coveragePercent == null ? DASH : <PctPill value={totals.coveragePercent} />}
                  </TableCell>
                  <TableCell align="right" sx={NUM_CELL_SX}>
                    {totals.compliancePercent == null ? DASH : <PctPill value={totals.compliancePercent} />}
                  </TableCell>
                  <TableCell align="right" sx={NUM_CELL_SX}>{fmt(totals.drCallAverage, 2)}</TableCell>
                  <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                  <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                  <TableCell align="right" sx={UNAVAIL_CELL_SX}>{DASH}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableFooter
            page={safePage}
            pageSize={ROWS_PER_PAGE}
            total={groups.length}
            onPageChange={setPage}
          />
        </Box>
      ) : null}
    </Box>
  );
}
